import React, { useMemo } from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import { fmtEur } from "./format";

const HORIZONS = [30, 60, 90];

// rowFor picks the furthest-out forecast point from a series-detail response:
// GET /api/v1/forecast-explorer/series/{name}?horizon=N (bare) -> { series: [...] }.
// Fetching once per horizon (rather than one fetch sliced by day offset) means
// each row is exactly the model's own horizon-N forecast, not an interpolation.
function rowFor(horizon, data) {
  const series = data?.series ?? [];
  const forecastPoints = series.filter((d) => d.forecast != null);
  const last = forecastPoints[forecastPoints.length - 1];
  return {
    horizon,
    expected: last?.forecast ?? null,
    best: last?.lower ?? null,
    worst: last?.upper ?? null,
  };
}

/**
 * ForecastTableBlock - 30/60/90-day forecast checkpoints for a series.
 *
 * Expected = forecast (point estimate), Best = lower band, Worst = upper band
 * (per the Task-0 binding table).
 */
const ForecastTableBlock = ({ title = "Forecast", binding = {}, period }) => {
  const { seriesName = "total" } = binding;
  // Fixed set of hook calls (one per horizon in HORIZONS) - valid under the
  // Rules of Hooks since the horizon list itself never changes at runtime.
  const q30 = useBlockData(`/api/v1/forecast-explorer/series/${encodeURIComponent(seriesName)}`, {
    params: { horizon: 30 },
  });
  const q60 = useBlockData(`/api/v1/forecast-explorer/series/${encodeURIComponent(seriesName)}`, {
    params: { horizon: 60 },
  });
  const q90 = useBlockData(`/api/v1/forecast-explorer/series/${encodeURIComponent(seriesName)}`, {
    params: { horizon: 90 },
  });

  const queries = [q30, q60, q90];
  const isLoading = queries.some((q) => q.isLoading);
  const error = queries.map((q) => q.error).find(Boolean) || null;

  const rows = useMemo(
    () => HORIZONS.map((h, i) => rowFor(h, queries[i].data)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q30.data, q60.data, q90.data]
  );

  const columns = useMemo(
    () => [
      { Header: "Horizon", accessor: "horizon", Cell: ({ value }) => `${value}d` },
      { Header: "Expected", accessor: "expected", align: "right", Cell: ({ value }) => fmtEur(value) },
      { Header: "Best", accessor: "best", align: "right", Cell: ({ value }) => fmtEur(value) },
      { Header: "Worst", accessor: "worst", align: "right", Cell: ({ value }) => fmtEur(value) },
    ],
    []
  );

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error}>
      <FlexTable columns={columns} data={rows} getRowId={(row) => String(row.horizon)} />
    </BaseBlock>
  );
};

ForecastTableBlock.propTypes = {
  title: PropTypes.string,
  binding: PropTypes.shape({
    seriesName: PropTypes.string,
  }),
  period: PropTypes.string,
};

export default ForecastTableBlock;
